import {
  DebentureSeriesGeneralData,
  DebenturesGeneralData,
} from 'src/types/operations/debentures';
import { formatCNPJ } from 'src/utils/document';
import { formatAmountBRL } from 'src/utils/number';

export type FieldGrid = {
  xs?: number;
  sm?: number;
  md?: number;
};

export type GeneralDataFieldConfig = {
  title: string;
  value: string;
  grid: FieldGrid;
};

export type OperationDebenturesGeneralDataProps = {
  hashId: string;
};

export type GeneralDataFieldSkeletonProps = {
  title?: string;
};

export const PLACEHOLDER = '—';
export const SERIES_SKELETON_COUNT = 3;

const GRID_THIRD: FieldGrid = { xs: 12, sm: 6, md: 4 };
const GRID_HALF: FieldGrid = { xs: 12, sm: 6, md: 6 };
const GRID_QUARTER: FieldGrid = { xs: 12, sm: 6, md: 3 };

export function formatTextValue(value?: string | null): string {
  return value ?? PLACEHOLDER;
}

export function formatCNPJValue(value?: string | null): string {
  return value ? formatCNPJ(value) : PLACEHOLDER;
}

export function formatIntegerValue(value?: number | null): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

export function formatCurrencyValue(value?: number | null): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  return formatAmountBRL(value);
}

export function formatPercentValue(value?: number | null): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function buildGeneralFields(
  data?: DebenturesGeneralData,
): GeneralDataFieldConfig[] {
  return [
    {
      title: 'CNPJ da Securitizadora',
      value: formatCNPJValue(data?.cnpjSecuritizadora),
      grid: GRID_THIRD,
    },
    {
      title: 'Nº da Emissão',
      value: formatTextValue(data?.numeroEmissao),
      grid: GRID_THIRD,
    },
    {
      title: 'Série',
      value: formatTextValue(data?.serie),
      grid: GRID_THIRD,
    },
    {
      title: 'Empresa',
      value: formatTextValue(data?.empresa),
      grid: GRID_HALF,
    },
  ];
}

export function buildSeriesFields(
  series: DebentureSeriesGeneralData,
): GeneralDataFieldConfig[] {
  return [
    {
      title: 'ISIN',
      value: formatTextValue(series.isin),
      grid: GRID_QUARTER,
    },
    {
      title: 'Situação',
      value: formatTextValue(series.situacao),
      grid: GRID_QUARTER,
    },
    {
      title: 'Forma',
      value: formatTextValue(series.forma),
      grid: GRID_QUARTER,
    },
    {
      title: 'Classe',
      value: formatTextValue(series.classe),
      grid: GRID_QUARTER,
    },
    {
      title: 'Data de Emissão',
      value: formatTextValue(series.dataEmissao),
      grid: GRID_QUARTER,
    },
    {
      title: 'Data de Vencimento',
      value: formatTextValue(series.dataVencimento),
      grid: GRID_QUARTER,
    },
    {
      title: 'Garantia / Espécie',
      value: formatTextValue(series.garantiaEspecie),
      grid: GRID_QUARTER,
    },
    {
      title: 'Qtde Emitida',
      value: formatIntegerValue(series.quantidadeEmitida),
      grid: GRID_QUARTER,
    },
    {
      title: 'Valor Nominal Emissão',
      value: formatCurrencyValue(series.valorNominalEmissao),
      grid: GRID_QUARTER,
    },
  ];
}
